import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  loginRequest,
  PasswordChangeRequiredError,
  TwoFactorRequiredError,
  isForcedPasswordChangeResponse,
  isTwoFactorChallengeResponse,
  type LoginChallenge,
  me,
  refresh as apiRefresh,
} from "../services/auth";
import {
  decodeJwtIssuer,
  loadStoredTokens,
  persistTokens,
  SessionTokens,
  withComputedExpiry,
} from "../services/tokenStorage";
import { registerSessionExpiredHandler, storeLoginNotice } from "../services/session";
import { registerAuthRefreshHandler, extractApiErrorMessage } from "../services/api";
import { useModalOpen } from "../contexts/ModalOpenContext";
import { useRuntimeConfig } from "../contexts/RuntimeConfigContext";
import { getKeycloakIssuer, getLogoutUrl, getTokenUrl, keycloakClientId } from "../config/keycloak";

type AuthState = {
  tokens: SessionTokens | null;
  username: string | null;
  fullName: string | null;
  email: string | null;
  managedByIdp: boolean;
  roles: string[];
  reviewerOnly: boolean;
  isReady: boolean;
  login: (u: string, p: string, otpCode?: string) => Promise<void>;
  completeIdpLogin: (tokens: SessionTokens) => Promise<void>;
  logout: () => void;
};

const AuthCtx = createContext<AuthState | null>(null);

type ChannelMessage =
  | { type: "TOKENS_UPDATED"; tokens: SessionTokens; source: string }
  | { type: "LOGOUT"; source: string };

const defaultExpiryMessage = "Invalid or expired token. Please sign in again.";
const emailVerificationNotice =
  "Your email address still needs to be verified by Cyberlope IDM.";

const isEmailNotVerifiedError = (err: any) =>
  err?.err?.error?.code === "AUTH_EMAIL_NOT_VERIFIED" || err?.err?.code === "AUTH_EMAIL_NOT_VERIFIED";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionTokens | null>(() => loadStoredTokens());
  const [username, setUsername] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [managedByIdp, setManagedByIdp] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [isReady, setReady] = useState(false);
  const { clearAuthenticatedConfig, loadAuthenticatedConfig } = useRuntimeConfig();

  const [reauthOpen, setReauthOpen] = useState(false);
  const [reauthMessage, setReauthMessage] = useState(defaultExpiryMessage);
  const [reauthUsername, setReauthUsername] = useState("");
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthBusy, setReauthBusy] = useState(false);
  const [reauthFormError, setReauthFormError] = useState<string | null>(null);
  const [reauthChallenge, setReauthChallenge] = useState<LoginChallenge | null>(null);
  const [reauthOtp, setReauthOtp] = useState("");

  const refreshTimerRef = useRef<number | null>(null);
  const refreshPromiseRef = useRef<Promise<SessionTokens | null> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const instanceIdRef = useRef<string>(Math.random().toString(36).slice(2));
  const refreshFnRef = useRef<() => Promise<SessionTokens | null> | null>(null);
  const hasHydratedRef = useRef(false);
  const sessionRef = useRef<SessionTokens | null>(session);
  const lastProfileTokenRef = useRef<string | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (tok: SessionTokens | null, trigger: () => void) => {
      clearRefreshTimer();
      if (!tok) return;
      const leadMs = 60_000;
      const now = Date.now();
      const delay = Math.max(5_000, tok.expires_at - now - leadMs);
      refreshTimerRef.current = window.setTimeout(() => {
        trigger();
      }, delay);
    },
    [clearRefreshTimer]
  );

  const clearSession = useCallback(
    (options?: { broadcast?: boolean }) => {
      const shouldBroadcast = options?.broadcast ?? true;
      clearRefreshTimer();
      sessionRef.current = null;
      lastProfileTokenRef.current = null;
      persistTokens(null);
      setSession(null);
      setUsername(null);
      setRoles([]);
      setFullName(null);
      setEmail(null);
      setManagedByIdp(false);
      clearAuthenticatedConfig();
      if (shouldBroadcast) {
        channelRef.current?.postMessage({ type: "LOGOUT", source: instanceIdRef.current });
      }
    },
    [clearRefreshTimer, clearAuthenticatedConfig]
  );

  const beginReauth = useCallback(
    (message?: string, options?: { forceIdp?: boolean }) => {
      const onLoginScreen = window.location.pathname.startsWith("/login");
    const isIdpSession = options?.forceIdp || managedByIdp;
    const fallbackMessage = isIdpSession
      ? "Session expired. Please sign in with Keycloak."
      : defaultExpiryMessage;
    const reason = message?.trim() || fallbackMessage;
    if (isIdpSession) {
      clearSession();
      storeLoginNotice(reason);
      window.location.assign("/login");
      return;
    }
      if (onLoginScreen) {
        clearSession();
        setReauthOpen(false);
        return;
      }
      const fallbackUser = username ?? "";
      clearSession();
      setReauthUsername(fallbackUser);
      setReauthPassword("");
      setReauthMessage(reason);
      setReauthFormError(null);
      setReauthChallenge(null);
      setReauthOtp("");
      setReauthOpen(true);
    },
    [clearSession, username, managedByIdp]
  );

  const loadProfile = useCallback(
    async (accessToken: string, options?: { force?: boolean }) => {
      if (!options?.force && lastProfileTokenRef.current === accessToken) {
        return;
      }
      const profile = await me(accessToken);
      setUsername(profile.username);
      setRoles(profile.roles ?? []);
      setFullName(profile.full_name ?? null);
      setEmail(profile.email ?? null);
      setManagedByIdp(Boolean(profile.managed_by_idp));
      lastProfileTokenRef.current = accessToken;
    },
    []
  );

  const performLogin = useCallback(
    (u: string, p: string, otpCode?: string) =>
      loginRequest({ username: u, password: p, otp_code: otpCode }),
    []
  );

  const applySession = useCallback(
    (next: SessionTokens, opts?: { broadcast?: boolean }) => {
      sessionRef.current = next;
      persistTokens(next);
      setSession(next);
      scheduleRefresh(next, () => {
        refreshFnRef.current?.();
      });
      if (opts?.broadcast !== false) {
        channelRef.current?.postMessage({
          type: "TOKENS_UPDATED",
          tokens: next,
          source: instanceIdRef.current,
        });
      }
    },
    [scheduleRefresh]
  );

  const refreshTokens = useCallback(async () => {
    const currentSession = sessionRef.current;
    if (!currentSession) return null;
    const issuer = decodeJwtIssuer(currentSession.access_token);
    const keycloakIssuer = getKeycloakIssuer();
    const isIdpToken = Boolean(issuer && keycloakIssuer && issuer.startsWith(keycloakIssuer));
    if (managedByIdp || isIdpToken) {
      if (!currentSession.refresh_token) {
        beginReauth("Session expired. Please sign in with Keycloak.", { forceIdp: true });
        return null;
      }
      if (refreshPromiseRef.current) return refreshPromiseRef.current;
      const promise = (async () => {
        try {
          const tokenUrl = getTokenUrl();
          if (!tokenUrl || !keycloakClientId) {
            beginReauth("Session expired. Please sign in with Keycloak.", { forceIdp: true });
            return null;
          }
          const body = new URLSearchParams();
          body.set("grant_type", "refresh_token");
          body.set("client_id", keycloakClientId);
          body.set("refresh_token", currentSession.refresh_token);
          const resp = await fetch(tokenUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString(),
          });
          if (!resp.ok) {
            const text = await resp.text();
            console.warn("Keycloak refresh failed", text);
            beginReauth("Session expired. Please sign in with Keycloak.", { forceIdp: true });
            return null;
          }
          const rawTokens = await resp.json();
          const tokens = {
            ...rawTokens,
            refresh_token: rawTokens.refresh_token ?? currentSession.refresh_token,
          };
          const fresh = withComputedExpiry(tokens);
          applySession(fresh);
          setReauthOpen(false);
          try {
            await loadProfile(fresh.access_token);
            await loadAuthenticatedConfig(fresh.access_token);
          } catch (profileErr) {
            console.warn("Unable to refresh profile after token renewal", profileErr);
          }
          return fresh;
        } catch (err: any) {
          beginReauth(err?.message, { forceIdp: true });
          return null;
        } finally {
          refreshPromiseRef.current = null;
        }
      })();
      refreshPromiseRef.current = promise;
      return promise;
    }
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const promise = (async () => {
      try {
        const response = await apiRefresh(currentSession.refresh_token);
        const fresh = withComputedExpiry(response);
        applySession(fresh);
        setReauthOpen(false);
          try {
            await loadProfile(fresh.access_token);
            await loadAuthenticatedConfig(fresh.access_token);
          } catch (profileErr) {
            console.warn("Unable to refresh profile after token renewal", profileErr);
          }
        return fresh;
      } catch (err: any) {
        beginReauth(err?.message);
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();
    refreshPromiseRef.current = promise;
    return promise;
  }, [applySession, loadProfile, loadAuthenticatedConfig, beginReauth, managedByIdp]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    refreshFnRef.current = refreshTokens;
  }, [refreshTokens]);

  useEffect(() => {
    const handler = async (failedToken?: string | null) => {
      const currentToken = sessionRef.current?.access_token ?? null;
      if (failedToken && currentToken && failedToken !== currentToken) {
        return currentToken;
      }
      const res = await refreshTokens();
      return res?.access_token ?? null;
    };
    registerAuthRefreshHandler(handler);
    return () => registerAuthRefreshHandler(null);
  }, [refreshTokens]);

  useEffect(() => {
    const handler = (message?: string) => beginReauth(message);
    registerSessionExpiredHandler(handler);
    return () => registerSessionExpiredHandler(null);
  }, [beginReauth]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return undefined;
    }
    const channel = new BroadcastChannel("iec62443-auth");
    channelRef.current = channel;
    channel.onmessage = (event) => {
      const msg = event.data as ChannelMessage;
      if (!msg || msg.source === instanceIdRef.current) return;
      if (msg.type === "TOKENS_UPDATED") {
        applySession(msg.tokens, { broadcast: false });
      } else if (msg.type === "LOGOUT") {
        clearSession({ broadcast: false });
        beginReauth(defaultExpiryMessage);
      }
    };
    return () => {
      channel.close();
    };
  }, [applySession, clearSession, beginReauth]);

  useEffect(() => {
    let mounted = true;
    async function hydrate() {
      if (window.location.pathname.startsWith("/auth/callback")) {
        clearRefreshTimer();
        hasHydratedRef.current = true;
        setReady(true);
        return;
      }
      if (!session) {
        if (mounted) {
          clearRefreshTimer();
          hasHydratedRef.current = true;
          setReady(true);
        }
        return;
      }
      if (!hasHydratedRef.current) {
        setReady(false);
      }
      const issuer = decodeJwtIssuer(session.access_token);
      const keycloakIssuer = getKeycloakIssuer();
      if (issuer && keycloakIssuer && issuer.startsWith(keycloakIssuer)) {
        setManagedByIdp(true);
      }
      if (session.expires_at <= Date.now()) {
        const refreshed = await refreshTokens();
        if (mounted && !refreshed) {
          hasHydratedRef.current = true;
          setReady(true);
        }
        return;
      }
      try {
        await loadProfile(session.access_token);
        await loadAuthenticatedConfig(session.access_token);
      } catch (err: any) {
        if (isEmailNotVerifiedError(err)) {
          clearSession();
          storeLoginNotice(emailVerificationNotice);
          window.location.assign("/login");
          return;
        }
        if (mounted) {
          console.warn("Unable to load profile during hydration", err);
        }
      } finally {
        if (mounted) {
          scheduleRefresh(session, () => {
            refreshFnRef.current?.();
          });
          hasHydratedRef.current = true;
          setReady(true);
        }
      }
    }
    hydrate();
    return () => {
      mounted = false;
    };
  }, [session, loadProfile, loadAuthenticatedConfig, scheduleRefresh, clearRefreshTimer, refreshTokens]);

  const login = useCallback(
    async (u: string, p: string, otpCode?: string) => {
      const response = await performLogin(u, p, otpCode);
      if (isTwoFactorChallengeResponse(response)) {
        throw new TwoFactorRequiredError(response.challenge);
      }
      if (isForcedPasswordChangeResponse(response)) {
        throw new PasswordChangeRequiredError(response.password_change_token, response.expires_in);
      }
      const sessionTokens = withComputedExpiry(response);
      applySession(sessionTokens);
      await loadProfile(sessionTokens.access_token);
      await loadAuthenticatedConfig(sessionTokens.access_token);
      setReauthOpen(false);
    },
    [performLogin, applySession, loadProfile, loadAuthenticatedConfig]
  );

  const completeIdpLogin = useCallback(
    async (sessionTokens: SessionTokens) => {
      try {
        await loadProfile(sessionTokens.access_token);
        await loadAuthenticatedConfig(sessionTokens.access_token);
        applySession(sessionTokens);
      } finally {
        setReauthOpen(false);
      }
    },
    [applySession, loadProfile, loadAuthenticatedConfig]
  );

  const logout = useCallback(() => {
    storeLoginNotice("You have been signed out.");
    clearSession();
    setReauthOpen(false);
    if (managedByIdp) {
      const logoutUrl = getLogoutUrl();
      if (logoutUrl && keycloakClientId) {
        const redirectUri = `${window.location.origin}/login`;
        const url = new URL(logoutUrl);
        url.searchParams.set("client_id", keycloakClientId);
        url.searchParams.set("post_logout_redirect_uri", redirectUri);
        window.location.assign(url.toString());
        return;
      }
    }
    window.location.assign("/login");
  }, [clearSession, managedByIdp]);

  const reviewerOnly = useMemo(
    () =>
      roles.includes("reviewer") && !roles.includes("admin") && !roles.includes("user"),
    [roles]
  );

  const authValue = useMemo<AuthState>(
    () => ({
      tokens: session,
      username,
      fullName,
      email,
      managedByIdp,
      roles,
      reviewerOnly,
      isReady,
      login,
      completeIdpLogin,
      logout,
    }),
    [
      session,
      username,
      fullName,
      email,
      managedByIdp,
      roles,
      reviewerOnly,
      isReady,
      login,
      completeIdpLogin,
      logout,
    ]
  );

  const handleReauthSubmit = async () => {
    setReauthFormError(null);
    setReauthBusy(true);
    try {
      const response = await performLogin(
        reauthUsername,
        reauthPassword,
        reauthChallenge ? reauthOtp : undefined
      );
      if (isTwoFactorChallengeResponse(response)) {
        setReauthChallenge(response.challenge);
        setReauthOtp("");
        return;
      }
      if (isForcedPasswordChangeResponse(response)) {
        setReauthFormError("Password change is required. Please sign in from the login page.");
        storeLoginNotice("Password change is required. Please sign in again.");
        window.location.assign("/login");
        return;
      }
      const sessionTokens = withComputedExpiry(response);
      applySession(sessionTokens);
      await loadProfile(sessionTokens.access_token);
      await loadAuthenticatedConfig(sessionTokens.access_token);
      setReauthChallenge(null);
      setReauthOpen(false);
    } catch (err: any) {
      setReauthFormError(extractApiErrorMessage(err, "Re-authentication failed"));
    } finally {
      setReauthBusy(false);
    }
  };

  useModalOpen(reauthOpen);

  return (
    <>
      <AuthCtx.Provider value={authValue}>{children}</AuthCtx.Provider>
      <Dialog
        open={reauthOpen}
        disableEscapeKeyDown
        aria-labelledby="reauth-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="reauth-dialog-title">Session expired</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {reauthMessage}
          </Typography>
          <Stack spacing={2}>
            {reauthFormError && <Alert severity="error">{reauthFormError}</Alert>}
            <TextField
              label="Username"
              value={reauthUsername}
              onChange={(e) => setReauthUsername(e.target.value)}
              fullWidth
              disabled={reauthBusy}
            />
            <TextField
              label="Password"
              type="password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              fullWidth
              disabled={reauthBusy}
            />
            {reauthChallenge && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {reauthChallenge.type === "ENROLL" && reauthChallenge.qr_code_data_url && (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <img
                      src={reauthChallenge.qr_code_data_url}
                      alt="Authenticator enrollment QR"
                      style={{ width: 180, height: 180 }}
                    />
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                  {reauthChallenge.message}
                </Typography>
                <TextField
                  label="One-time password"
                  value={reauthOtp}
                  onChange={(e) => setReauthOtp(e.target.value)}
                  fullWidth
                  disabled={reauthBusy}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="inherit"
            onClick={() => {
              storeLoginNotice(defaultExpiryMessage);
              window.location.assign("/login");
            }}
          >
            Go to login
          </Button>
          <Button
            variant="contained"
            onClick={handleReauthSubmit}
            disabled={
              reauthBusy ||
              !reauthUsername ||
              !reauthPassword ||
              (reauthChallenge ? !reauthOtp : false)
            }
          >
            Sign in
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
